import * as React from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getVirtualProviderMatchLiveInfo } from '../../Api';
import { VirtualProviderMatchSummaryDto } from '../../models/dtos/VirtualProviderCatalogDto';
import { formatKickoff, liveActionLabel, MatchTeams, stateLabel } from './VirtualMatchesTable';

export default function VirtualMatchEditDialog(props: {
    match: VirtualProviderMatchSummaryDto | null;
    leagueName?: string | null;
    onClose: () => void;
}) {
    const [liveUrl, setLiveUrl] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!props.match) return;
        let cancelled = false;
        setLiveUrl(null);
        setError('');
        setLoading(true);
        getVirtualProviderMatchLiveInfo(props.match.id)
            .then(details => { if (!cancelled) setLiveUrl(details.liveUrl); })
            .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load the live match page'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [props.match]);

    return (
        <Dialog open={props.match != null} onClose={props.onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit match</DialogTitle>
            <DialogContent dividers>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                {props.match ? (
                    <Stack spacing={2}>
                        <Box>
                            <MatchTeams match={props.match} size={32} />
                            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                <Chip size="small" label={stateLabel(props.match.state)} />
                                <Chip size="small" variant="outlined" label={formatKickoff(props.match.startDate)} />
                                {props.leagueName ? <Chip size="small" variant="outlined" label={props.leagueName} /> : null}
                            </Stack>
                        </Box>
                        <Typography color="text.secondary">
                            Open the dedicated live match page to watch or manage this match.
                        </Typography>
                        {loading ? <Box sx={{ textAlign: 'center' }}><CircularProgress size={24} /></Box> : null}
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>Close</Button>
                <Button
                    variant="contained"
                    startIcon={<OpenInNewIcon />}
                    disabled={!liveUrl}
                    onClick={() => liveUrl && window.open(liveUrl, '_blank', 'noopener,noreferrer')}
                >
                    {props.match ? liveActionLabel(props.match.state) : 'Live match'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
